import { Trip } from '51trips-frontend-core'
import classNames from 'classnames'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import { getAgencyAssetUrl, getAssetUrl } from '../utils/AssetsCDN.ts'
import getLogoTripBookSvg from '../utils/getLogoTripBookSvg.ts'
import { getOptimizedUrl } from '../utils/GetOptimizedUrl.ts'

type TripBookMiniatureProps = {
    trip: Trip
    orderColourID: string
    padding?: boolean
    size?: string
    widthPx?: number
    isRealistic?: boolean
    rounded?: string
    showSpine?: boolean
}

export const TripBookMiniature: React.FC<TripBookMiniatureProps> = ({
                                                                        trip,
                                                                        orderColourID,
                                                                        padding = true,
                                                                        size = 'normal',
                                                                        widthPx = 331,
                                                                        isRealistic = false,
                                                                        rounded = 'rounded-t-2xl',
                                                                        showSpine = false
                                                                    }) => {
    const [tripbookColorHex, setTripbookColorHex] = useState<string>('#000000')
    const colors = useSelector((state: any) => state.config.config.colors)
    const heightPx = (widthPx * 3) / 4

    // ----------------------------------------------------------
    // ✅ Resolver color
    // ----------------------------------------------------------
    useEffect(() => {
        const colorToDisplay =
            trip.agency?.color?.hex ||
            trip.tourOperator?.color?.hex ||
            orderColourID ||
            trip.colorHex ||
            trip.colorID
        const color = colors?.find(
            (c) => c.title === colorToDisplay || c.id === colorToDisplay || c.hexColor === colorToDisplay
        )

        if (color) {
            setTripbookColorHex(color.hexColor)
        } else if (colorToDisplay?.startsWith('#')) {
            setTripbookColorHex(colorToDisplay)
        }
    }, [colors])

    // ----------------------------------------------------------
    // ✅ Resolver logos
    // ----------------------------------------------------------
    const resolveLogos = () => {
        const baseStyle = {
            maxWidth: `${Math.round(widthPx / 9)}px`,
            filter: 'brightness(0) invert(1)',
            transform: 'rotate(90deg)'
        }

        if (trip.tourOperator?.logoUrl) {
            return {
                spinner: (
                    <img
                        style={{ ...baseStyle, maxWidth: `${Math.round(widthPx / 12)}px` }}
                        src={trip.tourOperator.logoContrastUrl || trip.tourOperator.logoUrl}
                    />
                ),
                bottom: (
                    <img
                        style={{ width: '100%', height: `${Math.round(widthPx / 25)}px`, objectFit: 'contain' }}
                        src={trip.tourOperator.logoUrl}
                    />
                )
            }
        }

        if (trip.agency?.useLogoInCover && trip.agency.logoName) {
            return {
                spinner: <img style={baseStyle} src={getAssetUrl('Logo_51T_RGB.png')} />,
                bottom: (
                    <img
                        style={{ width: '100%', height: `${Math.round(widthPx / 20)}px`, objectFit: 'contain' }}
                        src={getAgencyAssetUrl(trip.agency.logoName)}
                        loading="lazy"
                    />
                )
            }
        }

        if (trip.agency) {
            return {
                spinner: <img style={baseStyle} src={getAssetUrl('Logo_51T_RGB.png')} />,
                bottom: (
                    <div
                        style={{
                            color: 'black',
                            fontSize: `${Math.round(widthPx / 65)}px`,
                            textAlign: 'center',
                            textTransform: 'uppercase'
                        }}
                    >
                        {trip.agency.name}
                    </div>
                )
            }
        }
        return {
            spinner: <img style={baseStyle} src={getAssetUrl('Logo_51T_RGB.png')} />,
            bottom: (
                <img
                    style={{
                        height: `${Math.round(widthPx / 50)}px`,
                        objectFit: 'contain'
                    }}
                    src={getAssetUrl('Logo_51T_RGB.png')}
                />
            )
        }
    }

    const { spinner, bottom } = resolveLogos()

    // ----------------------------------------------------------
    // ✅ RENDER DESIGN VERSION 1
    // ----------------------------------------------------------
    const renderDesignV1 = () => (
        <div className="bg-neutralBlack-100 p-2">
            <div
                style={{ width: widthPx, height: heightPx }}
                className={classNames(['rounded-[8px]', 'bg-neutralBlack-100', padding && 'm-3'])}
            >
                <div style={{ width: widthPx, height: heightPx }} className="rounded-[8px] flex flex-row">
                    <div
                        className="w-[10%] rounded-l-[4px]"
                        style={{ height: heightPx, backgroundColor: tripbookColorHex }}
                    />
                    <div className="flex flex-col justify-between items-center bg-white rounded-xl w-[90%]">
                        {/* Logo + title */}
                        <div
                            className="flex flex-col justify-center items-center bg-white w-full"
                            style={{ height: '40%', fontSize: '6px', color: tripbookColorHex }}
                        >
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: getLogoTripBookSvg(tripbookColorHex, undefined, 25, 20)
                                }}
                            />
                            <div
                                className={
                                    size === 'mini'
                                        ? "font-medium  text-[5px]"
                                        : "font-medium  text-[11px]"
                                }
                            >
                                {trip.title}
                            </div>
                        </div>

                        {/* Cover image */}
                        <div style={{ height: '68%', width: '100%' }}>
                            <img
                                className="w-full h-full object-cover rounded-r-[4px]"
                                src={trip.coverImageURL}
                                alt={trip.title}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    // ----------------------------------------------------------
    // ✅ RENDER DESIGN VERSION 2
    // ----------------------------------------------------------
    const startYear = trip.startDate?.getFullYear()

    const renderDesignV2 = () => (
        <div
            className={classNames([
                rounded,
                !isRealistic ? 'bg-neutralBlack-100' : '',
                'p-0 flex justify-center items-center relative'
            ])}
        >
            {showSpine && isRealistic ? (
                <div
                    className="ml-[2%] w-[6%] rounded-l-[4px] flex flex-col bg-white"
                    style={{ height: heightPx, boxShadow: '0px 1px 3.7px -1px #0000001A inset' }}
                >
                    <div
                        className="w-[100%] h-full flex flex-col justify-between"
                        style={{ boxShadow: '0px 1px 3.7px -1px #0000001A inset' }}
                    >
                        {/* Icono + título vertical */}
                        <div className="mt-[100%] flex flex-col items-center">
                            <svg
                                width={`${Math.round(widthPx / 40)}px`}
                                height={`${Math.round(widthPx / 40)}px`}
                                viewBox="0 0 800 809"
                            >
                                <path fill={tripbookColorHex} d="..." />
                            </svg>

                            <p
                                style={{
                                    writingMode: 'vertical-rl',
                                    textOrientation: 'mixed',
                                    marginTop: 10,
                                    color: '#000',
                                    fontWeight: 700,
                                    fontSize: `${Math.round(widthPx / 65)}px`,
                                    lineHeight: '15px'
                                }}
                            >
                <span
                    style={{
                        textTransform: 'uppercase',
                        fontFamily: 'Lato',
                        height: `${Math.round(widthPx / 3.6)}px`,
                        overflow: 'hidden'
                    }}
                >
                  {trip.title} {startYear ? `| ${startYear}` : ''}
                </span>
                            </p>
                        </div>

                        {/* Fondo de color con logo */}
                        <div
                            style={{
                                width: '100%',
                                overflow: 'hidden',
                                position: 'relative',
                                backgroundColor: tripbookColorHex,
                                height: '30%',
                                borderRadius: '7px 7px 0 0',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center'
                            }}
                        >
                            {spinner}
                        </div>
                    </div>
                </div>
            ) : null}
            <div
                style={{ width: widthPx, height: heightPx }}
                className={classNames([
                    'rounded-[2px]',
                    !isRealistic
                        ? 'bg-neutralBlack-100'
                        : 'rounded-[2px] border border-neutralBlack-200 overflow-hidden',
                    padding && 'm-3'
                ])}
            >
                {/* Layout principal */}
                <div
                    style={{
                        width: widthPx,
                        height: heightPx,
                        boxShadow: !isRealistic ? '0px 1px 3.7px -1px #0000001A' : 'none'
                    }}
                    className={classNames('flex flex-row', !isRealistic && 'bg-white')}
                >
                    {/* Lomo */}
                    {!isRealistic ? (
                        <div
                            className="ml-[2%] w-[10%] rounded-l-[4px] flex flex-col bg-white"
                            style={{ height: heightPx, boxShadow: '0px 1px 3.7px -1px #0000001A inset' }}
                        >
                            <div
                                className="w-[80%] h-full flex flex-col justify-between"
                                style={{ boxShadow: '0px 1px 3.7px -1px #0000001A inset' }}
                            >
                                {/* Icono + título vertical */}
                                <div className="mt-[100%] flex flex-col items-center">
                                    <svg
                                        width={`${Math.round(widthPx / 40)}px`}
                                        height={`${Math.round(widthPx / 40)}px`}
                                        viewBox="0 0 800 809"
                                    >
                                        <path fill={tripbookColorHex} d="..." />
                                    </svg>

                                    <p
                                        style={{
                                            writingMode: 'vertical-rl',
                                            textOrientation: 'mixed',
                                            marginTop: 10,
                                            color: '#000',
                                            fontWeight: 700,
                                            fontSize: `${Math.round(widthPx / 65)}px`,
                                            lineHeight: '15px'
                                        }}
                                    >
                    <span
                        style={{
                            textTransform: 'uppercase',
                            fontFamily: 'Lato',
                            height: `${Math.round(widthPx / 3.6)}px`,
                            overflow: 'hidden'
                        }}
                    >
                      {trip.title} {startYear ? `| ${startYear}` : ''}
                    </span>
                                    </p>
                                </div>

                                {/* Fondo de color con logo */}
                                <div
                                    style={{
                                        width: '150%',
                                        left: '-25%',
                                        position: 'relative',
                                        backgroundColor: tripbookColorHex,
                                        height: '30%',
                                        borderRadius: '500px 500px 0 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    {spinner}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div
                            className={classNames([
                                isRealistic ? 'w-[4%] bg-white' : 'w-[5%]',
                                'rounded-l-[2px] flex flex-col justify-end bg-transparent'
                            ])}
                            style={{
                                height: heightPx,
                                boxShadow: !isRealistic ? '0px 1px 3.7px -1px rgba(0,0,0,0.1) inset' : 'none'
                            }}
                        >
                            <div
                                className={classNames([
                                    'h-full flex flex-col justify-end',
                                    isRealistic ? 'w-full' : 'w-[50%]'
                                ])}
                                style={{
                                    background: `
                    linear-gradient(
                      to left,
                      rgba(0, 0, 0, 0.08) 0%,
                      rgba(0, 0, 0, 0.06) 20%,
                      rgba(0, 0, 0, 0.02) 50%,
                      rgba(0, 0, 0, 0.06) 80%,
                      rgba(0, 0, 0, 0.06) 100%
                    )
                  `
                                }}
                            >
                                <div
                                    style={{
                                        height: '30%',
                                        borderRadius: '0px 500px 0px 0px',
                                        background: `linear-gradient(
        to right,
        ${tripbookColorHex} 0%,
        ${tripbookColorHex}E5 40%,
        ${tripbookColorHex}D8 100%
      )`
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Contenido principal */}
                    <div className="flex flex-col justify-between items-center bg-white w-[100%]">
                        {/* Título */}
                        <div
                            className="flex flex-col items-center justify-center mt-[5%] mb-1 w-full"
                            style={{ height: '20%' }}
                        >
                            <div
                                className="font-light font-['Lato'] uppercase"
                                style={{
                                    fontSize: `${Math.round(widthPx / (trip.title?.length < 17 ? 15 : 23))}px`,
                                    textAlign: 'center',
                                    width: '90%',
                                    overflow: 'hidden',
                                    display: '-webkit-box',
                                    WebkitBoxOrient: 'vertical',
                                    WebkitLineClamp: 1
                                }}
                            >
                                {trip.title}
                            </div>

                            <div
                                className="font-cormorant font-light uppercase text-black cursor-default"
                                style={{ fontSize: `${Math.round(widthPx / 35)}px` }}
                            >
                                {trip.subtitle ? `${trip.subtitle} | ` : ''}
                                <span>{startYear}</span>
                            </div>
                        </div>

                        {/* Imagen + logo inferior */}
                        <div
                            className="flex flex-col items-center justify-center w-full"
                            style={{ height: '75%' }}
                        >
                            <img
                                className="w-[87%] h-[90%] object-cover "
                                src={getOptimizedUrl(trip.coverImageURL, 2160)}
                                alt=""
                                loading="lazy"
                                onError={(e) => (e.currentTarget.src = getAssetUrl('backgroundCoverNoImage.jpg'))}
                            />

                            <div
                                className="bg-white relative"
                                style={{ padding: '0.5% 1%', bottom: `${Math.round(widthPx / 40)}px` }}
                            >
                                {bottom}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )

    const renderDesignV3 = () => {
        let titleFontSize =
            trip.title?.length < 17 ? Math.round(widthPx / 15) : Math.round(widthPx / 23)

        if (
            trip.otherPersonalization?.coverTitleStyle === 'Playful' ||
            trip.otherPersonalization?.coverTitleStyle === 'Modern'
        ) {
            titleFontSize = titleFontSize * 1.2
        }
        return (
            <div className={classNames([rounded, 'p-0 flex justify-center items-center relative'])}>
                {showSpine ? (
                    <div
                        className="ml-[2%] w-[6%] flex flex-col "
                        style={{
                            height: heightPx,
                            boxShadow: '0px 1px 3.7px -1px #0000001A inset',
                            backgroundColor: tripbookColorHex
                        }}
                    >
                        <div
                            className="w-[100%] h-full flex flex-col justify-between"
                            style={{ boxShadow: '0px 1px 3.7px -1px #0000001A inset' }}
                        >
                            {/* Icono + título vertical */}
                            <div className="mt-[100%] flex flex-col items-center">
                                <svg
                                    width={`${Math.round(widthPx / 40)}px`}
                                    height={`${Math.round(widthPx / 40)}px`}
                                    viewBox="0 0 800 809"
                                >
                                    <path fill={tripbookColorHex} d="..." />
                                </svg>

                                <p
                                    style={{
                                        writingMode: 'vertical-rl',
                                        textOrientation: 'mixed',
                                        marginTop: 10,
                                        color: '#000',
                                        fontWeight: 700,
                                        fontSize: `${Math.round(widthPx / 65)}px`,
                                        lineHeight: '15px'
                                    }}
                                >
                  <span
                      style={{
                          textTransform: 'uppercase',
                          fontFamily: 'Lato',
                          color: '#fff',
                          height: `${Math.round(widthPx / 3.6)}px`,
                          overflow: 'hidden'
                      }}
                  >
                    {trip.title} {startYear ? `| ${startYear}` : ''}
                  </span>
                                </p>
                            </div>

                            {/* Fondo de color con logo */}
                            <div
                                style={{
                                    width: '100%',
                                    overflow: 'hidden',
                                    position: 'relative',
                                    height: '30%',
                                    borderRadius: '7px 7px 0 0',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center'
                                }}
                            >
                                {spinner}
                            </div>
                        </div>
                    </div>
                ) : null}
                <div
                    style={{ width: widthPx, height: heightPx }}
                    className={classNames([
                        'rounded-[2px]',
                        !isRealistic
                            ? 'bg-neutralBlack-100'
                            : 'rounded-[2px] border border-neutralBlack-200 overflow-hidden',
                        padding && 'm-3'
                    ])}
                >
                    {/* Layout principal */}
                    <div
                        style={{
                            width: widthPx,
                            boxShadow: 'none',
                            backgroundImage:
                                ' linear-gradient(rgba(0, 0, 0, 0.18),rgba(0, 0, 0, 0.18)),url(' +
                                getOptimizedUrl(trip.coverImageURL, 2160) +
                                ')',
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat',
                            aspectRatio: '4/3'
                        }}
                        className={classNames('flex flex-row bg-white')}
                    >
                        {/* Lomo */}
                        <div
                            className={classNames([
                                isRealistic ? 'w-[4%]' : 'w-[5%]',
                                'rounded-l-[2px] flex flex-col justify-end bg-transparent'
                            ])}
                            style={{
                                height: heightPx,
                                boxShadow: !isRealistic ? '0px 1px 3.7px -1px rgba(0,0,0,0.1) inset' : 'none'
                            }}
                        >
                            <div
                                className={classNames([
                                    'h-full flex flex-col justify-end',
                                    isRealistic ? 'w-full' : 'w-[50%]'
                                ])}
                                style={{
                                    background: `
                    linear-gradient(
                      to left,
                  rgba(0, 0, 0, 0.12) 0%,
                      rgba(0, 0, 0, 0.06) 20%,
                      rgba(0, 0, 0, 0.02) 50%,
                      rgba(0, 0, 0, 0.12) 80%
                    )
                  `
                                }}
                            ></div>
                        </div>

                        {/* Contenido principal */}
                        <div className="flex flex-col justify-between items-center w-[100%]">
                            {/* Título */}
                            <div
                                className={classNames([
                                    'flex flex-col items-center  mb-1 w-full',
                                    trip.otherPersonalization?.coverTitlePosition === 'center'
                                        ? 'mt-[30%]'
                                        : 'mt-[10%]'
                                ])}
                                style={{ height: '20%' }}
                            >
                                <div
                                    className={classNames([
                                        'uppercase cursor-default',
                                        {
                                            'font-lato font-light':
                                                !trip.otherPersonalization?.coverTitleStyle ||
                                                trip.otherPersonalization.coverTitleStyle === 'default' ||
                                                trip.otherPersonalization.coverTitleStyle === 'Elegant',
                                            'font-varela': trip.otherPersonalization?.coverTitleStyle === 'Classic',
                                            'font-gaegu': trip.otherPersonalization?.coverTitleStyle === 'Playful',
                                            'font-bebao': trip.otherPersonalization?.coverTitleStyle === 'Modern'
                                        }
                                    ])}
                                    style={{
                                        fontSize: `${titleFontSize}px`,
                                        textAlign: 'center',
                                        width: '90%',
                                        overflow: 'hidden',
                                        display: '-webkit-box',
                                        WebkitBoxOrient: 'vertical',
                                        WebkitLineClamp: 1,
                                        color: trip.otherPersonalization?.coverTextColor || '#FFFFFF'
                                    }}
                                >
                                    {trip.title}
                                </div>

                                <div
                                    className="font-cormorant font-light uppercase text-white cursor-default"
                                    style={{
                                        fontSize: `${Math.round(widthPx / 35)}px`,
                                        color: trip.otherPersonalization?.coverTextColor || '#FFFFFF'
                                    }}
                                >
                                    {trip.subtitle ? `${trip.subtitle} | ` : ''}
                                    <span>{startYear}</span>
                                </div>
                            </div>

                            {/* Imagen + logo inferior */}
                            <div className="flex flex-col items-center justify-center w-full">
                                <div className="relative mb-[10%] invert brightness-0">{bottom}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }

    // ✅ Seleccionar diseño
    switch (trip?.otherPersonalization?.coverVersion) {
        case '1':
            return renderDesignV1()
        case '2':
            return renderDesignV2()
        case '3':
            return renderDesignV3()
        default:
            return renderDesignV3()
    }
}
